import { Dialog } from "@tellescope/design-system";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import type { ManagedContentRecord } from "@tellescope/types-models";
import { ArticleBlockRenderer } from "./ArticleBlockRenderer";
import { PdfPreviewFrame, pdfPreviewDialogClassName } from "./PdfPreviewFrame";

interface ContentPreviewDialogProps {
  /** The content being previewed */
  content: ManagedContentRecord | null;
  /** The presigned URL for attachments (PDF/Video) - not needed for Article */
  attachmentUrl: string | null;
  /** Whether the preview is currently loading */
  isLoading?: boolean;
  /** Callback when the dialog is closed */
  onClose: () => void;
}

/**
 * Dialog for previewing content (Article, PDF, Video).
 *
 * - Articles: Displays HTML content directly
 * - PDFs: Displays in an iframe using the PDF preview service
 * - Videos: Displays in a video player
 */
export function ContentPreviewDialog({
  content,
  attachmentUrl,
  isLoading = false,
  onClose,
}: ContentPreviewDialogProps) {
  const { t } = useTranslation("content");

  // Open if we have content (Article) or content with attachment URL (PDF/Video), or loading
  const isOpen = !!content || isLoading;
  const isPdfPreview = !isLoading && content?.type === "PDF" && !!attachmentUrl;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Content className={isPdfPreview ? pdfPreviewDialogClassName : "max-w-4xl"}>
        <Dialog.Header>
          <Dialog.Title className="truncate pr-8">{content?.title ?? t("Loading...")}</Dialog.Title>
        </Dialog.Header>

        <Dialog.Body className="flex-1">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : content ? (
            <ContentPreviewContent content={content} attachmentUrl={attachmentUrl} />
          ) : null}
        </Dialog.Body>
      </Dialog.Content>
    </Dialog>
  );
}

interface ContentPreviewContentProps {
  content: ManagedContentRecord;
  attachmentUrl: string | null;
}

function ContentPreviewContent({ content, attachmentUrl }: ContentPreviewContentProps) {
  const { t } = useTranslation("content");

  // Article - display blocks array
  if (content.type === "Article") {
    if (content.blocks && content.blocks.length > 0) {
      return <ArticleBlockRenderer blocks={content.blocks} />;
    }

    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground">
        <p>{t("No content available")}</p>
      </div>
    );
  }

  // PDF - display in iframe using PDF preview service
  if (content.type === "PDF") {
    if (!attachmentUrl) {
      return (
        <div className="flex items-center justify-center h-64 text-muted-foreground">
          <p>{t("PDF not available")}</p>
        </div>
      );
    }

    return <PdfPreviewFrame url={attachmentUrl} title={content.title} />;
  }

  // Video - display in video player
  if (content.type === "Video") {
    if (!attachmentUrl) {
      return (
        <div className="flex items-center justify-center h-64 text-muted-foreground">
          <p>{t("Video not available")}</p>
        </div>
      );
    }

    return (
      <div className="aspect-video w-full">
        <video src={attachmentUrl} controls className="w-full h-full rounded-lg" title={content.title}>
          <track kind="captions" />
        </video>
      </div>
    );
  }

  // Fallback for unknown types
  return (
    <div className="flex items-center justify-center h-64 text-muted-foreground">
      <p>{t("Preview not available for this content type")}</p>
    </div>
  );
}
